/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'nouislider/dist/nouislider.css';
import './Settings.css';
import { ChevronLeft } from 'react-feather';
import { Offcanvas as BootstrapOffcanvas } from 'bootstrap';
import { initializeDistanceSlider, initializeAgeRangeSlider, destroySlider } from '../../utils/sliders';
import SubscriptionOffcanvas from '../Subscription/SubscriptionOffcanvas';
import { settingsService } from '../../services/settingsService';
import { userService } from '../../services/userService';

import { useDispatch, useSelector } from 'react-redux';
import { getAuth, signOut } from 'firebase/auth';
import { clearAccountDeletion, clearAuth, setAgeSearch, updateAuth } from '../../store/slices/authSlice';
import { clearCache } from '../../store/slices/cacheSlice';
import { resetLocationState } from '../../store/slices/locationSlice';
import useReverseGeocode from '../../hooks/useReverseGeocode';
import { Modal, Spin, Select, Input, Form, Button, Checkbox } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Option } = Select;

const Settings = () => {
    const navigate = useNavigate();
    const offcanvasRefs = useRef({
        phone: useRef(null),
        email: useRef(null),
        location: useRef(null),
        report: useRef(null)
    });
    const slidersRef = useRef({ distance: null, age: null });
    const dispatch = useDispatch();
    const auth = getAuth();
    const authState = useSelector(state => state.auth);
    const { address } = useReverseGeocode(authState.location);
    const slidersInitialized = useRef(false);
    const { t, i18n } = useTranslation();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [reportType, setReportType] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [isSubmittingReport, setIsSubmittingReport] = useState(false);
    const [showSubscriptionOffcanvas, setShowSubscriptionOffcanvas] = useState(false);
    const [selectedSubscriptionType, setSelectedSubscriptionType] = useState('plus');
    const currentLanguage = i18n.resolvedLanguage;
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshMessage, setRefreshMessage] = useState('');
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [isCancellingDeletion, setIsCancellingDeletion] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [hasAcknowledgedDeletion, setHasAcknowledgedDeletion] = useState(false);

    const clearLocalSession = async () => {
        await signOut(auth);
        dispatch(clearAuth());
        dispatch(clearCache());
        dispatch(resetLocationState());
        localStorage.clear();
        navigate('/', { replace: true });
    };

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            // Attendre un peu pour montrer le spinner
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Sign out from Firebase
            await signOut(auth);

            // Clear Redux states (auth, cache, ET location pour sécurité)
            dispatch(clearAuth());
            dispatch(clearCache());
            dispatch(resetLocationState()); // Réinitialiser la localisation et permissions GPS
            localStorage.clear();

            // Navigate to welcome page
            navigate('/', { replace: true });
        } catch (error) {
            console.error('Error signing out:', error);
        } finally {
            setIsLoggingOut(false);
        }
    };

    const handleLogoutClick = () => {
        Modal.confirm({
            title: 'Confirmation',
            icon: <ExclamationCircleOutlined />,
            content: 'Êtes-vous sûr de vouloir vous déconnecter ?',
            okText: 'Oui',
            cancelText: 'Non',
            onOk: handleLogout,
        });
    };

    const requestAccountDeletion = async () => {
        setIsDeletingAccount(true);
        try {
            const response = await settingsService.requestAccountDeletion();
            const scheduledFor = new Date(response.accountDeletion.scheduledFor).toLocaleDateString(currentLanguage);
            await clearLocalSession();
            Modal.success({
                title: t('settings.account.deletionScheduledTitle'),
                content: t('settings.account.deletionScheduledMessage', { date: scheduledFor })
            });
        } catch (error) {
            if (error.response?.data?.code === 'RECENT_LOGIN_REQUIRED') {
                Modal.warning({
                    title: t('settings.account.recentLoginTitle'),
                    content: t('settings.account.recentLoginMessage'),
                    okText: t('settings.account.reconnect'),
                    onOk: clearLocalSession
                });
            } else {
                Modal.error({
                    title: t('settings.account.deletionErrorTitle'),
                    content: error.response?.data?.message || t('settings.account.deletionErrorMessage')
                });
            }
        } finally {
            setIsDeletingAccount(false);
        }
    };

    const handleDeleteAccountClick = () => {
        setDeleteConfirmation('');
        setHasAcknowledgedDeletion(false);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        if (isDeletingAccount) return;

        setIsDeleteModalOpen(false);
        setDeleteConfirmation('');
        setHasAcknowledgedDeletion(false);
    };

    const requiredDeletePhrase = t('settings.account.deleteConfirmationPhrase');
    const canConfirmDeletion = deleteConfirmation.trim() === requiredDeletePhrase
        && hasAcknowledgedDeletion;

    const cancelAccountDeletion = async () => {
        setIsCancellingDeletion(true);
        try {
            const response = await settingsService.cancelAccountDeletion();
            dispatch(updateAuth(response.user));
            dispatch(clearAccountDeletion({ status: response.user?.status || 'Active' }));
            Modal.success({
                title: t('settings.account.deletionCancelledTitle'),
                content: t('settings.account.deletionCancelledMessage')
            });
        } catch (error) {
            if (error.response?.status === 404) {
                // Le serveur est la source de vérité : si aucune suppression
                // n'est programmée, nettoyer une éventuelle donnée Redux obsolète.
                dispatch(clearAccountDeletion({ status: 'Active' }));
                Modal.success({
                    title: t('settings.account.deletionCancelledTitle'),
                    content: t('settings.account.deletionCancelledMessage')
                });
                return;
            }
            Modal.error({
                title: t('settings.account.deletionErrorTitle'),
                content: error.response?.data?.message || t('settings.account.deletionErrorMessage')
            });
        } finally {
            setIsCancellingDeletion(false);
        }
    };

    const handleSubscriptionClick = (plan) => {
        setSelectedSubscriptionType(plan);
        setShowSubscriptionOffcanvas(true);
    };

    const handleReportSubmit = async (values) => {
        setIsSubmittingReport(true);

        try {
            await settingsService.submitReport({
                type: values.type,
                description: values.description,
                userId: authState.uid
            });

            // Réinitialiser le formulaire
            setReportType('');
            setReportDescription('');
            
            // Fermer l'offcanvas
            const offcanvas = document.getElementById('reportOffcanvas');
            const bsOffcanvas = BootstrapOffcanvas.getInstance(offcanvas);
            bsOffcanvas.hide();

            // Afficher un message de succès
            Modal.success({
                title: 'Merci pour votre signalement',
                content: 'Nous avons bien reçu votre rapport et nous l\'examinerons dans les plus brefs délais.',
            });

        } catch (error) {
            console.error('Error submitting report:', error);
            Modal.error({
                title: 'Erreur',
                content: 'Une erreur est survenue lors de l\'envoi du rapport. Veuillez réessayer.',
            });
        } finally {
            setIsSubmittingReport(false);
        }
    };

    const handleLanguageChange = () => {
        const newLanguage = currentLanguage === 'fr' ? 'en' : 'fr';
        i18n.changeLanguage(newLanguage);
    };

    // Fonction pour actualiser les données d'abonnement depuis le serveur
    const handleRefreshSubscription = async () => {
        setIsRefreshing(true);
        setRefreshMessage('');

        try {
            // Récupérer les données utilisateur depuis le serveur
            const userData = await userService.getUserByFbid(authState.uid);

            if (userData) {
                // Mettre à jour le Redux store avec les nouvelles données
                const updateData = {};

                // Mise à jour de l'abonnement si présent
                if (userData.packageEndDate) {
                    updateData.packageEndDate = userData.packageEndDate;
                }
                if (userData.level) {
                    updateData.level = userData.level;
                }

                // Mise à jour des invitations si présent
                if (userData.invitations !== undefined) {
                    updateData.invitations = userData.invitations;
                }

                // Appliquer les mises à jour dans Redux
                if (Object.keys(updateData).length > 0) {
                    dispatch(updateAuth(updateData));
                    setRefreshMessage('Informations mises à jour avec succès !');
                } else {
                    setRefreshMessage('Aucune mise à jour disponible.');
                }
            } else {
                setRefreshMessage('Impossible de récupérer les données.');
            }
        } catch (error) {
            console.error('Erreur lors du rafraîchissement:', error);
            setRefreshMessage('Erreur lors de la mise à jour. Réessayez.');
        } finally {
            setIsRefreshing(false);
            // Masquer le message après 3 secondes
            setTimeout(() => setRefreshMessage(''), 3000);
        }
    };

    useEffect(() => {
        // Initialize offcanvas elements
        Object.values(offcanvasRefs.current).forEach(ref => {
            if (ref.current) {
                new BootstrapOffcanvas(ref.current, {
                    backdrop: true,
                    keyboard: true
                });
            }
        });

        // Initialize select using vanilla JavaScript instead of jQuery
        const select = document.querySelector('.custom-image-select');
        if (select) {
            // Create a custom dropdown
            select.addEventListener('change', function(e) {
                const selectedOption = this.options[this.selectedIndex];
                const thumbnail = selectedOption.getAttribute('data-thumbnail');
                const text = selectedOption.text;
                
                // Update the display
                const displayElement = this.parentElement.querySelector('.selected-option');
                if (!displayElement) {
                    const display = document.createElement('div');
                    display.className = 'selected-option d-flex align-items-center';
                    this.parentElement.appendChild(display);
                }
                displayElement.innerHTML = `<img src="${thumbnail}" className="flag-icon me-2" />${text}`;
            });
        }

        // Initialize sliders only once
        if (!slidersInitialized.current) {
            // Copie de la ref dans une variable locale pour que le cleanup utilise toujours la même référence
            const sliders = slidersRef.current;
            const timer = setTimeout(() => {
                // Initialize sliders
                if (!sliders.distance) {
                    sliders.distance = initializeDistanceSlider();
                }
                if (!sliders.age) {
                    const ageSlider = initializeAgeRangeSlider(authState.ageSearch);
                    if (ageSlider) {
                        sliders.age = ageSlider;
                        ageSlider.noUiSlider.on('change', function(values) {
                            dispatch(setAgeSearch({
                                min: parseInt(values[0]),
                                max: parseInt(values[1])
                            }));
                        });
                    }
                }
                slidersInitialized.current = true;
            }, 0);

            return () => {
                clearTimeout(timer);
                if (sliders.distance) {
                    destroySlider(sliders.distance);
                }
                if (sliders.age) {
                    destroySlider(sliders.age);
                }
            };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="settings-container">
            {isLoggingOut && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <Spin size="large" />
                </div>
            )}

        <div className="page-wrapper">
                {/* Header */}
                <header className="header header-fixed">
                    <div className="container">
                        <div className="header-content">
                            <div className="left-content me-3">
                                <a href="#" className="back-btn" onClick={() => navigate(-1)}>
                                    <ChevronLeft className="icon" />
                                </a>
                                <h6 className="title">{t('settings.title')}</h6>
                            </div>
                            <div className="mid-content">
                            </div>
                            <div className="right-content">
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <div className=" content-body">
                    <div className="container">
                        <div className="row gy-3 mb-4">
                            <div className="col-12">
                                <a onClick={() => handleSubscriptionClick('plus')} className="dz-box plus">
                                    <div className="logo-img">
                                    <img src={require('../../assets/images/fulllogo_transparent_nobuffer.png')} alt="" />
                                    {/* <img src={process.env.PUBLIC_URL + '/assets/img/w3tinder/tinder2.png'} alt="" /> */}
                                        <span className="badge">PLUS</span>
                                    </div>
                                    <p>{t('settings.account.subscription')}</p>
                                </a>
                            </div>
                            <div className="col-12">
                                <a onClick={() => handleSubscriptionClick('gold')} className="dz-box gold">
                                    <div className="logo-img">
                                        <img src={require('../../assets/images/fulllogo_transparent_nobuffer.png')} alt="" />
                                        <span className="badge">GOLD</span>
                                    </div>
                                    <p>{t('settings.account.subscription')}</p>
                                </a>
                            </div>
                            <div className="col-12">
                                <a onClick={() => handleSubscriptionClick('platinum')} className="dz-box platinum">
                                    <div className="logo-img">
                                        <img src={require('../../assets/images/fulllogo_transparent_nobuffer.png')} alt="" />
                                        <span className="badge">PLATINUM</span>
                                    </div>
                                    <p>{t('settings.account.subscription')}</p>
                                </a>
                            </div>
                        </div>
                            {/* Refresh Subscription Section */}
                            <div className="section mt-3 mb-3">
                                <div className="card style-4">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div className="flex-grow-1 me-2">
                                                <h6 className="mb-1" style={{ fontSize: '14px', fontWeight: '600' }}>
                                                    Synchroniser l'abonnement
                                                </h6>
                                                <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                                                    Après un paiement
                                                </p>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-sm d-flex align-items-center"
                                                onClick={handleRefreshSubscription}
                                                disabled={isRefreshing}
                                                style={{
                                                    borderRadius: '6px',
                                                    padding: '8px 12px',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {isRefreshing ? (
                                                    <Spin size="small" />
                                                ) : (
                                                    <>
                                                        <i className="fa-solid fa-rotate" style={{ fontSize: '12px' }}></i>
                                                        <span className="ms-1">Actualiser</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        {refreshMessage && (
                                            <div
                                                className={`w-100 p-2 ${refreshMessage.includes('succès') ? 'alert-success' : refreshMessage.includes('Erreur') ? 'alert-danger' : 'alert-info'}`}
                                                role="alert"
                                                style={{
                                                    fontSize: '12px',
                                                    borderRadius: '6px',
                                                    marginBottom: '0'
                                                }}
                                            >
                                                {refreshMessage}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* <h6 className="title">{t('settings.account.title')}</h6>
                            <div className="card style-4">
                                <div className="card-header">
                                    <h6 className="title mb-0 font-14">{t('settings.account.phoneNumber')}</h6>
                                </div>
                                <div className="card-body">
                                    <a href="#" className="setting-input" data-bs-toggle="offcanvas" data-bs-target="#phoneOffcanvas">
                                        <i className="icon dz-flex-box feather icon-phone-call"></i>
                                        <span>{authState.phoneNumber || t('settings.account.addPhoneNumber')}</span>
                                        <i className="icon feather dz-flex-box icon-chevron-right ms-auto me-0"></i>
                                    </a>
                                </div>
                            </div>

                            <div className="card style-4">
                                <div className="card-header">
                                    <h6 className="title mb-0 font-14">{t('settings.account.emailAddress')}</h6>
                                </div>
                                <div className="card-body">
                                    <a href="#" className="setting-input" data-bs-toggle="offcanvas" data-bs-target="#emailOffcanvas">
                                        <i className="icon dz-flex-box feather icon-mail"></i>
                                        <span>{authState.email || t('settings.account.addEmail')}</span>
                                        <i className="icon feather dz-flex-box icon-chevron-right ms-auto me-0"></i>
                                    </a>
                                </div>
                            </div>

                            <h6 className="title">{t('settings.discovery.title')}</h6>
                            <div className="card style-4">
                                <div className="card-header">
                                    <h6 className="title mb-0 font-14">{t('settings.discovery.location')}</h6>
                                </div>
                                <div className="card-body">
                                    <a href="#" className="setting-input" data-bs-toggle="offcanvas" data-bs-target="#locationOffcanvas">
                                        <i className="icon dz-flex-box feather icon-map-pin"></i>
                                        <span>
                                            {loading ? t('settings.discovery.loading') : (address || t('settings.discovery.setLocation'))}
                                        </span>
                                        <i className="icon feather dz-flex-box icon-chevron-right ms-auto me-0"></i>
                                    </a>
                                </div>
                            </div> */}

                            {/* Report Section */}
                            <h6 className="title">{t('settings.support.title')}</h6>
                            <div className="card style-4">
                                <div className="card-header">
                                    <h6 className="title mb-0 font-14">{t('settings.support.reportProblem')}</h6>
                                </div>
                                <div className="card-body">
                                    <a href="#" className="setting-input" data-bs-toggle="offcanvas" data-bs-target="#reportOffcanvas">
                                        <i className="icon dz-flex-box fa-solid fa-flag"></i>
                                        <span>{t('settings.support.reportProblem')}</span>
                                        <i className="icon feather dz-flex-box icon-chevron-right ms-auto me-0"></i>
                                    </a>
                                </div>
                            </div>

                            {/* Language Settings */}
                            <h6 className="title">{t('settings.language')}</h6>
                            <div className="card style-4">
                                <div className="card-body">
                                        <div className="d-flex language justify-content-between align-items-center">
                                            <div>
                                                <h6 className="title mb-0">Français</h6>
                                            </div>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={currentLanguage === 'fr'}
                                                    onChange={handleLanguageChange}
                                                />
                                            </div>
                                            <div>
                                                <h6 className="title mb-0">English</h6>
                                            </div>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={currentLanguage === 'en'}
                                                    onChange={handleLanguageChange}
                                                />
                                            </div>
                                        </div>
                                </div>
                            </div>

                            {/* Account deletion already scheduled */}
                            {authState.accountDeletion?.status === 'scheduled' && (
                                <div className="mt-5 mb-3">
                                    <div className="card border-warning">
                                        <div className="card-body">
                                            <h6>{t('settings.account.deletionPending')}</h6>
                                            <p className="text-muted">
                                                {t('settings.account.deletionPendingUntil', {
                                                    date: new Date(authState.accountDeletion.scheduledFor).toLocaleDateString(currentLanguage)
                                                })}
                                            </p>
                                            <button
                                                className="btn btn-warning w-100"
                                                onClick={cancelAccountDeletion}
                                                disabled={isCancellingDeletion}
                                            >
                                                {isCancellingDeletion ? <Spin size="small" /> : t('settings.account.cancelDeletion')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Logout Button */}
                            <div className="mt-5 mb-4">
                                <button 
                                    className="btn btn-danger w-100 btn-gradient-danger"
                                    style={{
                                        borderRadius: '10px',
                                        padding: '12px',
                                        fontSize: '16px',
                                        fontWeight: '600'
                                    }}
                                    onClick={handleLogoutClick}
                                >
                                    {t('settings.account.logout')}
                                </button>
                            </div>

                            {/* Deliberately secondary account management actions */}
                            {authState.accountDeletion?.status !== 'scheduled' && (
                                <details className="account-danger-zone mb-4">
                                    <summary>{t('settings.account.manageAccount')}</summary>
                                    <div className="account-danger-zone__content">
                                        <p>{t('settings.account.manageAccountHint')}</p>
                                        <button
                                            type="button"
                                            className="account-delete-link"
                                            onClick={handleDeleteAccountClick}
                                            disabled={isDeletingAccount}
                                        >
                                            {t('settings.account.deleteAccount')}
                                        </button>
                                    </div>
                                </details>
                            )}
                        </div>
                    </div>

                <Modal
                    title={t('settings.account.deleteConfirm')}
                    open={isDeleteModalOpen}
                    onCancel={closeDeleteModal}
                    onOk={requestAccountDeletion}
                    okText={t('settings.account.confirmDelete')}
                    cancelText={t('settings.account.cancel')}
                    confirmLoading={isDeletingAccount}
                    okButtonProps={{ danger: true, disabled: !canConfirmDeletion }}
                    cancelButtonProps={{ disabled: isDeletingAccount }}
                    closable={!isDeletingAccount}
                    maskClosable={!isDeletingAccount}
                >
                    <p>{t('settings.account.deleteGracePeriod')}</p>
                    <p><strong>{t('settings.account.deleteDataWarning')}</strong></p>
                    <label className="account-delete-confirmation-label" htmlFor="delete-account-confirmation">
                        {t('settings.account.deleteConfirmationInstruction', { phrase: requiredDeletePhrase })}
                    </label>
                    <Input
                        id="delete-account-confirmation"
                        value={deleteConfirmation}
                        onChange={(event) => setDeleteConfirmation(event.target.value)}
                        placeholder={requiredDeletePhrase}
                        autoComplete="off"
                        disabled={isDeletingAccount}
                    />
                    <Checkbox
                        className="account-delete-acknowledgement"
                        checked={hasAcknowledgedDeletion}
                        onChange={(event) => setHasAcknowledgedDeletion(event.target.checked)}
                        disabled={isDeletingAccount}
                    >
                        {t('settings.account.deleteAcknowledgement')}
                    </Checkbox>
                </Modal>

                {/* Phone Offcanvas */}
                <div className="offcanvas offcanvas-bottom" 
                     tabIndex="-1" 
                     id="phoneOffcanvas" 
                     ref={offcanvasRefs.current.phone}>
                    <button type="button" className="btn-close drage-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                    <div className="offcanvas-header share-style m-0 pb-0">
                        <h6 className="title">{t('settings.account.phoneNumber')}</h6>
                    </div>
                    <div className="offcanvas-body overflow-visible">
                        <form>
                            <div className="input-group dz-select">
                                <div className="input-group-text"> 
                                    <div>
                                        <select className="form-control custom-image-select image-select">
                                            <option data-thumbnail="/assets/images/flags/australia.png">+61</option>
                                            <option data-thumbnail="/assets/images/flags/india.png">+91</option>
                                            <option data-thumbnail="/assets/images/flags/uae.png">+971</option>
                                            <option data-thumbnail="/assets/images/flags/us.png">+1</option>
                                        </select>
                                    </div>
                                </div>
                                <input type="number" className="form-control" defaultValue={authState.phoneNumber} />
                            </div>

                            <a href="#" className="btn btn-gradient w-100 dz-flex-box btn-shadow rounded-xl" data-bs-dismiss="offcanvas">{t('settings.save')}</a>
                        </form>
                    </div>
                </div>

                {/* Email Offcanvas */}
                <div className="offcanvas offcanvas-bottom" 
                     tabIndex="-1" 
                     id="emailOffcanvas" 
                     ref={offcanvasRefs.current.email}>
                    <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                    <div className="offcanvas-header">
                        <h6 className="title">{t('settings.account.emailAddress')}</h6>
                    </div>
                    <div className="offcanvas-body">
                        <input type="email" className="form-control" defaultValue={authState.email} />
                        <button className="btn btn-gradient w-100 dz-flex-box btn-shadow rounded-xl mt-3" data-bs-dismiss="offcanvas">{t('settings.save')}</button>
                    </div>
                </div>

                {/* Location Offcanvas */}
                <div className="offcanvas offcanvas-bottom" 
                     tabIndex="-1" 
                     id="locationOffcanvas" 
                     ref={offcanvasRefs.current.location}>
                    <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                    <div className="offcanvas-header">
                        <h6 className="title">{t('settings.discovery.location')}</h6>
                    </div>
                    <div className="offcanvas-body">
                        <input type="text" className="form-control" defaultValue={address} readOnly />
                        <button className="btn btn-gradient w-100 dz-flex-box btn-shadow rounded-xl mt-3" data-bs-dismiss="offcanvas">{t('settings.save')}</button>
                    </div>
                </div>

                {/* Report Offcanvas */}
                <div className="offcanvas offcanvas-bottom" 
                     tabIndex="-1" 
                     id="reportOffcanvas" 
                     ref={offcanvasRefs.current.report}>
                    <button type="button" className="btn-close drage-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                    <div className="offcanvas-header share-style m-0 pb-0">
                        <h6 className="title">{t('settings.support.reportProblem')}</h6>
                    </div>
                    <div className="offcanvas-body overflow-visible">
                        <Form onFinish={handleReportSubmit} layout="vertical">
                            <Form.Item
                                name="type"
                                rules={[{ required: true, message: t('settings.support.selectProblemType') }]}
                            >
                                <Select
                                    placeholder={t('settings.support.selectProblemType')}
                                    onChange={(value) => setReportType(value)}
                                    value={reportType}
                                    size="large"
                                    style={{ width: '100%' }}
                                >
                                    <Option value="bug">{t('settings.support.problemTypes.bug')}</Option>
                                    <Option value="feature">{t('settings.support.problemTypes.feature')}</Option>
                                    <Option value="other">{t('settings.support.problemTypes.other')}</Option>
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name="description"
                                rules={[{ required: true, message: t('settings.support.describeProblem') }]}
                            >
                                <TextArea
                                    placeholder={t('settings.support.describeProblem')}
                                    onChange={(e) => setReportDescription(e.target.value)}
                                    value={reportDescription}
                                    rows={4}
                                    size="large"
                                    style={{ 
                                        resize: 'none',
                                        borderRadius: '8px'
                                    }}
                                />
                            </Form.Item>
                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={isSubmittingReport}
                                    block
                                    size="large"
                                    style={{
                                        height: '48px',
                                        borderRadius: '8px',
                                        fontWeight: '500'
                                    }}
                                >
                                    {isSubmittingReport ? t('settings.support.submitting') : t('settings.support.submit')}
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </div>

                <SubscriptionOffcanvas
                    show={showSubscriptionOffcanvas}
                    onClose={() => setShowSubscriptionOffcanvas(false)}
                    planType={selectedSubscriptionType}
                />
            </div>
        </div>
    );
};

export default Settings;
